import { createSignal, For, Show } from "solid-js";

import { confirmManualPayment, lookupManualPayment } from "~/lib/businessLogic";

const money = ( n: number | null | undefined ) => `£${( n ?? 0 ).toFixed( 2 )}`;

// Bank transfers bypass the Stripe webhook, so the automation a card payment
// triggers never runs. Paste the invoice number off the Xero invoice, check what
// it pays for, then run the same steps Stripe would have.
export const ManualPayment = () => {
	const [invoiceNumber, setInvoiceNumber] = createSignal( "" );
	const [looking, setLooking] = createSignal( false );
	const [confirming, setConfirming] = createSignal( false );
	const [override, setOverride] = createSignal( false );
	const [error, setError] = createSignal<string | null>( null );
	const [found, setFound] = createSignal<any>( null );
	const [done, setDone] = createSignal<any>( null );

	const reset = () => {
		setFound( null );
		setDone( null );
		setError( null );
		setOverride( false );
	};

	const lookup = async () => {
		reset();
		setLooking( true );

		try {
			const r = await lookupManualPayment( invoiceNumber() );

			if ( r?.error ) setError( r.error );
			else setFound( r );
		} catch ( err ) {
			console.error( "Invoice lookup failed", err );
			setError( "Lookup failed - check the server log." );
		} finally {
			setLooking( false );
		}
	};

	const confirm = async () => {
		setConfirming( true );
		setError( null );

		try {
			const r = await confirmManualPayment( invoiceNumber() );

			if ( r?.error ) {
				setError( r.error );
			} else {
				setDone( r );
				setFound( null );
				setInvoiceNumber( "" );
			}
		} catch ( err ) {
			console.error( "Manual payment failed", err );
			setError( "Confirmation failed - check the server log." );
		} finally {
			setConfirming( false );
		}
	};

	// Anything flagged needs an explicit acknowledgement before Confirm unlocks.
	const blocked = () => !!found()?.warnings?.length && !override();

	return (
		<div class="white-box space-y-4">
			<h1 class="text-xl font-bold">Record a bank transfer</h1>

			<p class="text-sm text-gray-500">
				Paste the Xero invoice number (e.g. RR-260903-33803) to run the same automation a card payment would.
			</p>

			<div class="flex gap-2">
				<input
					type="text"
					value={invoiceNumber()}
					placeholder="RR-260903-33803"
					onInput={e => { setInvoiceNumber( e.currentTarget.value ); reset(); }}
					onKeyDown={e => { if ( e.key === "Enter" ) lookup(); }}
					class="flex-1 rounded border border-gray-300 px-3 py-2 text-sm font-mono focus:border-sky-600 focus:outline-none"
				/>
				<button
					type="button"
					onClick={lookup}
					disabled={looking() || !invoiceNumber().trim()}
					class="rias bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed">
					{looking() ? "Looking…" : "Look up"}
				</button>
			</div>

			<Show when={error()}>
				<p class="rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error()}</p>
			</Show>

			<Show when={done()}>
				<div class="rounded border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
					<p class="font-semibold">{done().invoiceNumber} confirmed</p>
					<p>{done().summary}</p>
					<p class="text-xs mt-1">
						{done().markedPaidInXero
							? "Payment recorded in Xero and the automation has run."
							: "Already settled in Xero - the automation has run."}
					</p>
				</div>
			</Show>

			<Show when={found()}>
				<div class="rounded border border-gray-300 divide-y divide-gray-200 text-sm">
					<div class="px-3 py-2 flex justify-between gap-3">
						<span class="font-mono">{found().invoiceNumber}</span>
						<span class="text-gray-500">{found().xeroStatus}</span>
					</div>

					<div class="px-3 py-2">
						<p class="font-semibold">{found().summary}</p>
						<Show when={found().user}>
							<p class="text-gray-500">
								{found().user.name} · {found().user.email}
							</p>
						</Show>
						<For each={found().lineDescriptions}>
							{d => <p class="text-gray-500 text-xs mt-1">{d}</p>}
						</For>
					</div>

					<div class="px-3 py-2 flex justify-between">
						<span class="text-gray-500">Total</span>
						<span>{money( found().total )}</span>
					</div>

					<div class="px-3 py-2 flex justify-between">
						<span class="text-gray-500">Outstanding</span>
						<span classList={{ "text-gray-500": ( found().amountDue ?? 0 ) <= 0 }}>
							{money( found().amountDue )}
						</span>
					</div>

					<Show when={found().effect} fallback={
						<div class="px-3 py-2 text-gray-500">
							Confirming will not change this record.
						</div>
					}>
						<div class="px-3 py-2">
							<span class="text-gray-500">On confirm: </span>{found().effect}
						</div>
					</Show>
				</div>

				<Show when={found().warnings?.length}>
					<div class="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 space-y-1">
						<For each={found().warnings}>{w => <p>{w}</p>}</For>

						<label class="flex items-center gap-2 pt-1 font-medium">
							<input type="checkbox" checked={override()} onChange={e => setOverride( e.currentTarget.checked )} />
							I have read the above - confirm anyway
						</label>
					</div>
				</Show>

				<button
					type="button"
					onClick={confirm}
					disabled={confirming() || blocked()}
					class="rias w-full bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed">
					{confirming()
						? "Working…"
						: ( found().amountDue ?? 0 ) > 0
							? `Confirm payment of ${money( found().amountDue )}`
							: "Run the automation"}
				</button>
			</Show>
		</div>
	);
};
